/* /vendor-standards — Faitharama vendor rules + product submission.
   Mike's directive Feb 2026:
   - Faitharama welcomes everyone (you don't have to be Christian to join)
   - We proclaim Jesus Christ in OUR OWN products (books, courses)
   - Vendors who want their product listed must follow the clean/family-friendly rules
   - Minimum 20% commission to be considered (50% is preferred)
   - Banned: gambling, pornography, other-religion services (yoga classes etc.), anything not kid-safe */
import { useEffect, useState } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import {
  Shield, CheckCircle2, XCircle, Handshake, Mail, Send, Loader2, Sparkles,
} from "lucide-react";

const G   = "#2E9F3A";
const O   = "#F39A1F";
const RED = "#C9202E";
const SKY = "#E0F2FE";
const INK = "#0F172A";

const ALLOWED = [
  "Wellness, supplements, clean-ingredient products",
  "Books, courses, training, and digital downloads",
  "Family-friendly tools, hobbies, and home goods",
  "Christian books, sermons, devotionals, and worship music",
  "Outdoor, RC hobby, and gear we vet personally",
  "Business and marketing services that help our members earn",
  "Clothing — organic cotton, bamboo, or chemical-free fabrics only",
  "Bedding, towels, and home linens — organic or natural fibers only",
];

const BANNED = [
  "Gambling, betting, casinos, or sports books",
  "Pornography, adult content, or sexual products",
  "Revealing, immodest, or sexualized clothing",
  "Clothing or linens treated with toxic dyes, formaldehyde, or chemical finishes",
  "Other-religion services or instruction (yoga, meditation classes, tarot, astrology, etc.)",
  "Tobacco, vaping, recreational drugs, or alcohol",
  "Anything that isn't family-friendly or kid-safe",
  "Multi-level schemes with + qualification hoops",
];

/* The active "we're hunting for these" wishlist. Update as gaps get filled. */
const ESPECIALLY_WELCOME = [
  { label: "Organic Bedding & Linens",      detail: "Sheets, comforters, pillowcases — organic cotton, bamboo, hemp, or linen. We currently have NO affiliate partner here. Bring one." },
  { label: "Organic / Bamboo Clothing",     detail: "Modest, family-friendly cuts. Men's, women's, and kids'. No chemical finishes. Bamboo and organic cotton preferred." },
  { label: "Non-Toxic Skincare & Beauty",   detail: "Mineral makeup, clean skincare, plant-based personal care. USDA-organic a plus." },
  { label: "Christian Apparel & Gifts",     detail: "Modest tees, hats, jewelry, home decor with biblical messaging or imagery." },
  { label: "Kitchen & Cookware (Clean)",    detail: "Cast iron, stainless, glass, ceramic — no Teflon, no plastic linings." },
  { label: "Water Filtration & Storage",    detail: "Whole-house filters, gravity systems, glass water bottles, alkaline pitchers." },
  { label: "Home-School Curriculum",        detail: "Faith-friendly K-12 materials, language programs, history, math, and science." },
  { label: "Survival, Prepper, Self-Reliance", detail: "Long-term food storage, water purification, emergency kits, off-grid gear." },
  { label: "Books & Audiobooks",            detail: "Christian non-fiction, biography, children's books, family devotionals." },
];

export default function VendorStandardsPage() {
  const [form, setForm] = useState({
    company: "", contact: "", email: "", product: "", commission: "", website: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    document.title = "Vendor Standards & Product Submission — Faitharama.com";
  }, []);

  function update(field, v) { setForm((p) => ({ ...p, [field]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.company.trim() || !form.email.trim() || !form.product.trim()) {
      setError("Please fill in your company, email, and product name.");
      return;
    }
    setLoading(true);
    try {
      // Reuse the public lead-capture endpoint with a vendor-application tag.
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/free-guide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${form.contact || form.company} — VENDOR APPLICATION`,
          email: form.email.trim().toLowerCase(),
          source: `vendor-submit | company=${form.company} | product=${form.product} | commission=${form.commission} | website=${form.website} | notes=${form.notes.slice(0, 200)}`,
        }),
      });
      if (!res.ok) throw new Error("submit-failed");
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please email mike@faitharama.com instead.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="vendor-standards-page">
      <SiteNav />

      {/* ═══ HERO ═══ */}
      <section className="border-b-2" style={{ background: SKY, borderColor: G }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: G }} />
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: RED }}>
            ★ FAITHARAMA VENDOR STANDARDS
          </div>
          <h1 className="font-serif-display uppercase leading-[0.98]"
              style={{ color: INK, fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}>
            Open To Everyone.<br />
            <span style={{ color: G }}>Clean Standards.</span>
          </h1>
          <p className="text-lg md:text-2xl mt-5 max-w-3xl mx-auto leading-relaxed" style={{ color: INK }}>
            You don&apos;t have to be a Christian or a believer to be a Faitharama member, an affiliate, or a vendor. We proclaim Jesus Christ as Lord in <strong>our own</strong> books and courses — but every tool, every payout, and every product opportunity on this platform is open to anyone willing to keep it clean and family-friendly.
          </p>
        </div>
      </section>

      {/* ═══ THE RULES ═══ */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8">

          <div className="grid md:grid-cols-2 gap-6">

            {/* ALLOWED */}
            <div className="rounded-2xl border-4 p-6 md:p-7 shadow-md" style={{ borderColor: G, background: "#F0FAF1" }}>
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="w-7 h-7" style={{ color: G }} />
                <h2 className="font-serif-display text-2xl md:text-3xl uppercase" style={{ color: INK }}>
                  Welcome On The Platform
                </h2>
              </div>
              <ul className="space-y-3">
                {ALLOWED.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-base md:text-lg leading-relaxed" style={{ color: INK }}>
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: G }} />
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            {/* BANNED */}
            <div className="rounded-2xl border-4 p-6 md:p-7 shadow-md" style={{ borderColor: RED, background: "#FEF2F2" }}>
              <div className="flex items-center gap-3 mb-3">
                <XCircle className="w-7 h-7" style={{ color: RED }} />
                <h2 className="font-serif-display text-2xl md:text-3xl uppercase" style={{ color: INK }}>
                  Not Allowed
                </h2>
              </div>
              <ul className="space-y-3">
                {BANNED.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base md:text-lg leading-relaxed" style={{ color: INK }}>
                    <XCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: RED }} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ═══ ESPECIALLY WELCOME — wishlist ═══ */}
          <div className="mt-8 rounded-2xl border-4 p-6 md:p-8 shadow-md"
               style={{ borderColor: G, background: "linear-gradient(180deg,#F0FAF1 0%,#FFFFFF 60%)" }}
               data-testid="especially-welcome">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-7 h-7" style={{ color: G }} />
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: RED }}>
                ★ ESPECIALLY WELCOME
              </div>
            </div>
            <h2 className="font-serif-display uppercase leading-[1.05]"
                style={{ color: INK, fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)" }}>
              We&apos;re Actively Hunting For These.
            </h2>
            <p className="text-lg md:text-xl mt-3 max-w-3xl leading-relaxed" style={{ color: INK }}>
              If you sell any of the below — or know someone who does — we want to talk. Some of these gaps we&apos;ve been trying to fill for months (organic bedding chief among them). Bring it and we&apos;ll fast-track the review.
            </p>

            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5" data-testid="wishlist">
              {ESPECIALLY_WELCOME.map((w) => (
                <li key={w.label} className="flex items-start gap-3">
                  <CheckCircle2 className="w-7 h-7 flex-shrink-0 mt-0.5" style={{ color: G }} />
                  <div>
                    <div className="font-bold uppercase tracking-wide text-base md:text-lg" style={{ color: INK }}>
                      {w.label}
                    </div>
                    <div className="text-base md:text-lg leading-relaxed mt-1" style={{ color: INK, opacity: 0.85 }}>
                      {w.detail}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* ═══ COMMISSION RULE ═══ */}
          <div className="mt-8 rounded-2xl border-4 p-6 md:p-8 shadow-md text-center"
               style={{ borderColor: O, background: "linear-gradient(180deg,#FFF9EF 0%,#FEF6E3 100%)" }}
               data-testid="commission-rule">
            <Handshake className="w-10 h-10 mx-auto mb-3" style={{ color: O }} />
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: RED }}>
              ★ THE COMMISSION RULE
            </div>
            <h2 className="font-serif-display uppercase leading-[1.05]"
                style={{ color: INK, fontSize: "clamp(2rem, 4.2vw, 3rem)" }}>
              Be Generous With Our People.<br />
              <span style={{ color: G }}>Minimum 20%. 50/50 Is Preferred.</span>
            </h2>
            <p className="text-lg md:text-xl mt-4 max-w-3xl mx-auto leading-relaxed" style={{ color: INK }}>
              Faitharama splits its own product revenue 50/50 with our affiliates. We expect vendors to do the same — or close to it. <strong>Minimum 20% commission</strong> on every sale our members refer <em style={{ color: G }}>(that&apos;s 20% of YOUR profit on the sale — not of your gross revenue. We&apos;re asking for a fair share of your margin, nothing more.)</em>. Anything less and we won&apos;t list you, no matter how good the product is.
            </p>
            <p className="text-base md:text-lg italic mt-5 max-w-2xl mx-auto" style={{ color: INK, opacity: 0.8 }}>
              I&apos;m not trying to control free commerce. I&apos;m trying to make sure my people get paid fair for the work they do. — Mike
            </p>
          </div>

          {/* ═══ SUBMISSION FORM ═══ */}
          <div className="mt-10 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-8 items-start">

            {/* LEFT — intro */}
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: RED }}>
                ★ SUBMIT YOUR PRODUCT
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl uppercase leading-[1.05]" style={{ color: INK }}>
                Got A Product We Should Look At?
              </h2>
              <p className="text-lg md:text-xl mt-4 leading-relaxed" style={{ color: INK }}>
                Fill out the form and Mike will personally take a look. If it fits the standards above and the commission is fair, we&apos;ll add it to the Faitharama Market and tell our members.
              </p>
              <p className="text-base md:text-lg mt-4" style={{ color: INK, opacity: 0.85 }}>
                Or email Mike directly: <a href="mailto:mike@faitharama.com" className="underline font-bold" style={{ color: G }}>mike@faitharama.com</a>
              </p>
            </div>

            {/* RIGHT — form */}
            {!success ? (
              <form
                onSubmit={handleSubmit}
                data-testid="vendor-submit-form"
                className="bg-white rounded-2xl shadow-xl p-5 md:p-6 border-4 space-y-3"
                style={{ borderColor: G }}
              >
                <input type="text" required value={form.company}
                       onChange={(e) => update("company", e.target.value)}
                       placeholder="Company / Brand Name *"
                       data-testid="vendor-company"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <input type="text" value={form.contact}
                       onChange={(e) => update("contact", e.target.value)}
                       placeholder="Your Name (Contact Person)"
                       data-testid="vendor-contact"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <input type="email" required value={form.email}
                       onChange={(e) => update("email", e.target.value)}
                       placeholder="Your Best Email *"
                       data-testid="vendor-email"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <input type="text" required value={form.product}
                       onChange={(e) => update("product", e.target.value)}
                       placeholder="Product or Service Name *"
                       data-testid="vendor-product"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <input type="text" value={form.commission}
                       onChange={(e) => update("commission", e.target.value)}
                       placeholder="Commission You're Offering (e.g., 30%, 50/50)"
                       data-testid="vendor-commission"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <input type="url" value={form.website}
                       onChange={(e) => update("website", e.target.value)}
                       placeholder="Your Website / Product Page URL"
                       data-testid="vendor-website"
                       className="w-full px-4 py-3.5 rounded-md text-lg border-2"
                       style={{ borderColor: "#cbd5e1", color: INK }} />
                <textarea rows={3} value={form.notes}
                          onChange={(e) => update("notes", e.target.value)}
                          placeholder="Anything else we should know?"
                          data-testid="vendor-notes"
                          className="w-full px-4 py-3.5 rounded-md text-lg border-2 resize-none"
                          style={{ borderColor: "#cbd5e1", color: INK }} />
                <button type="submit" disabled={loading}
                        data-testid="vendor-submit-btn"
                        className="w-full px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.02] transition flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: O }}>
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>
                  ) : (
                    <><Send className="w-5 h-5" /> Submit For Review</>
                  )}
                </button>
                {error && (
                  <div className="text-sm font-bold text-center" style={{ color: RED }}>
                    {error}
                  </div>
                )}
                <div className="text-xs text-center pt-1" style={{ color: INK, opacity: 0.7 }}>
                  Mike reviews every submission personally. Expect a reply within 48 hours.
                </div>
              </form>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border-4 text-center"
                   style={{ borderColor: G }} data-testid="vendor-submit-success">
                <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color: G }} />
                <h3 className="font-serif-display text-2xl md:text-3xl uppercase mb-3" style={{ color: INK }}>
                  Got It. Thanks.
                </h3>
                <p className="text-base" style={{ color: INK }}>
                  Mike will personally review <strong>{form.product}</strong> and get back to you within 48 hours at <strong>{form.email}</strong>.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
